/**
 * Full-screen status states for the hotel checkout: preparing (loading), all
 * rooms unavailable, and the post-submit "request received" confirmation that
 * replaces the form column.
 */

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { AskElaraButton } from '#ui/AskElaraButton.js';
import { Button, Loader } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

/**
 * Centered loading state while `PrepareHotelCheckout` runs. No two-column
 * layout yet — there's nothing to show on the right rail until prices land,
 * and an empty summary column reads as broken.
 */
export function PreparingState(): React.ReactNode {
  return (
    <div className="flex h-[100vh] items-center justify-center px-6">
      <div className="flex max-w-md flex-col items-center gap-1 text-center">
        <Loader loading={true} />
        <h2 className="text-lg font-display mt-2">Preparing your booking</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          We're confirming the latest rates and availability with the hotel. This usually takes a few seconds.
        </p>
      </div>
    </div>
  );
}

/**
 * Rendered when every room came back unavailable from PriceCheck. Uses the
 * same centered single-column layout as `PreparingState` so the page reads
 * as a deliberate state rather than a broken two-column shell. `onAskElara`
 * is the next step the copy names: it opens the trip's chat with the question
 * already drafted.
 */
export function AllUnavailableState({
  droppedRoomNames,
  onBack,
  onAskElara,
}: {
  droppedRoomNames: string[];
  onBack: () => void;
  onAskElara?: () => void;
}): React.ReactNode {
  const heading =
    droppedRoomNames.length === 1 ? 'This room is no longer available' : 'These rooms are no longer available';
  return (
    <div className="flex h-[100vh] items-center justify-center px-6">
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <MaterialIcon icon="error" size={IconSizes.lg} className="text-destructive" />
        <h2 className="text-lg font-display mt-1">{heading}</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          {droppedRoomNames.length > 0
            ? `${droppedRoomNames.join(', ')} can no longer be booked at the rate you selected. Ask Elara to find alternatives.`
            : 'These rooms can no longer be booked at the rates you selected. Ask Elara to find alternatives.'}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {onAskElara ? <AskElaraButton onClick={onAskElara} /> : null}
          <Button variant="outline" onClick={onBack}>
            <MaterialIcon icon="arrow_back" size={IconSizes.xs} />
            <span>Back</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Designed replacement for a bare red error string when `PrepareHotelCheckout`
 * fails. The dominant case is re-opening checkout for a room that's already
 * booked — an expected state, not a failure — so it gets a calm "already booked"
 * treatment (no raw item id, green like the confirmed state) rather than red
 * error text. With the rate card now flipping to "Booked", this is a rare
 * safety net (e.g. a stale direct link). Anything else falls back to a generic,
 * still-designed error.
 */
export function CheckoutErrorState({
  message,
  onBack,
}: {
  message: string | undefined;
  onBack: () => void;
}): React.ReactNode {
  const alreadyBooked = message != null && /already attached to a booking/i.test(message);

  if (alreadyBooked) {
    return (
      <div className="flex h-[100vh] items-center justify-center px-6">
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-sm bg-green-100">
            <MaterialIcon icon="event_available" size={IconSizes.lg} className="text-success" weight="light" />
          </div>
          <h2 className="text-lg font-display mt-1">This room is already booked</h2>
          <p className="text-sm text-muted-foreground text-pretty">
            It’s already on your trip. To book another, use “Book another room” on the room’s rates.
          </p>
          <Button variant="outline" onClick={onBack} className="mt-2">
            <MaterialIcon icon="arrow_back" size={IconSizes.xs} />
            <span>Back to trip</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100vh] items-center justify-center px-6">
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <MaterialIcon icon="error" size={IconSizes.lg} className="text-destructive" />
        <h2 className="text-lg font-display mt-1">We couldn’t prepare your booking</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          {message ?? 'Something went wrong while confirming the latest rates. Please try again.'}
        </p>
        <Button variant="outline" onClick={onBack} className="mt-2">
          <MaterialIcon icon="arrow_back" size={IconSizes.xs} />
          <span>Back</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * Replaces the form column once the hotel booking is placed. The booking is made automatically
 * (Create-PNR via the PCI Booking relay) and the card on file secures the reservation, so this is a
 * completed-booking confirmation — not a request handed off to the team.
 */
/**
 * The post-booking screen. With `partialMessage` it is the outcome of an order that booked some rooms and
 * not others (no rollback — ELARA-1079): the same settled screen, amber instead of green, carrying the
 * server's sentence that says what is booked, which room did not book, and the next step — which
 * `onAskElara` makes a button: the trip's chat with the follow-up already drafted.
 */
export function HotelBookingConfirmedState({
  onBackToTrip,
  partialMessage,
  onAskElara,
}: {
  onBackToTrip: () => void;
  partialMessage?: string;
  onAskElara?: () => void;
}): React.ReactNode {
  return (
    <div className="space-y-3">
      <div
        className={cn(
          'flex items-center justify-center w-14 h-14 rounded-sm mb-6',
          partialMessage ? 'bg-amber-100' : 'bg-green-100',
        )}
      >
        <MaterialIcon
          icon="event_available"
          size={IconSizes.lg}
          className={partialMessage ? 'text-amber-700 dark:text-amber-400' : 'text-success'}
          weight="light"
        />
      </div>
      <h2 className="text-xl md:text-2xl font-display">
        {partialMessage ? 'Booking partly confirmed' : 'Booking confirmed'}
      </h2>
      <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
        {partialMessage ??
          'Your rooms are confirmed with the hotel and your card is securing the reservation. You’ll find the confirmation details and any payment schedule on your trip.'}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onBackToTrip}>
          <MaterialIcon icon="arrow_back" size={IconSizes.xs} />
          <span>Back to trip</span>
        </Button>
        {partialMessage && onAskElara ? <AskElaraButton onClick={onAskElara} /> : null}
      </div>
    </div>
  );
}
